import * as React from 'react';
import { jsx, css, keyframes } from '@emotion/react';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import { setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import type { BaseAnswerType } from '../../../../../../../chat/views/types';
import type { SimpleButtonGroupPropsType } from '../..';

const { tablet, mobile } = mediaQueries();

export const simpleButtonStyle = css`
  color: #212b60;
  font-size: 24px;
  font-weight: bold;
  line-height: 1.2;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 98px;
  padding: 0 0.2rem;
  background-color: #fff;
  border: 2px solid #041156;
  border-radius: 10px;
  box-shadow: 0 4px 0 #545971;
  position: relative;

  ${mobile(({ clamp }) => css`
    color: #041156;
    font-size: ${clamp('18px', '20px')};
    height: ${clamp('84px', '90px')};
    border: 1px solid #041156;
    border-radius: 6px;
    box-shadow: 0 2px 0 #272e51;
  `)}
`;

export const simpleButtonHoverStyle = css`
  background-color: #fff7c9;

  ${mobile(() => css`
    box-shadow: none;
    opacity: 1;
    transform: translateY(2px);
  `)}
`;

export const simpleButtonActiveStyle = css`
  color: #212b60;
  background-color: #fff7c9;
  box-shadow: none;
  opacity: 1;

  ${mobile(() => css`
    background-color: #fff;
  `)}
`;

export const simpleButtonAnsweredStyle = css`
  pointer-events: none;
`;

type SimpleButtonPropsType = Merge<SimpleButtonGroupPropsType & Required<BaseAnswerType> & {
  balloonContents?: React.ReactNode;
  isAnswered: boolean;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}>;

export const SimpleButton = ({
  keyName,
  id,
  value,
  textContents,
  gaEventName,
  balloonContents,
  isAnswered,
  onClickHandler: onClickHandlerFromProps,
  answers,
}: SimpleButtonPropsType) => {
  const [isActive, setIsActive] = React.useState<boolean>(null);

  const onClickHandler = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    // ボタンをアクティブに変更
    setIsActive(true);

    // ボタンのクリックイベントを実行
    onClickHandlerFromProps(event);
  }, [onClickHandlerFromProps]);

  return (
    <div
      css={css`
        padding: 0 0 4px;
        position: relative;
      `}
    >
      <button
        type="button"
        id={`ga_event_${gaEventName}_${id}`}
        value={value}
        onClick={onClickHandler}
        css={css`
          ${simpleButtonStyle}

          ${answers.length === 3 && css`
            font-size: 20px;

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              font-size: ${setAutomaticDesignBasedSize(20, 16)};
              padding: 0;
            `)}
          `}

          ${answers.length === 6 && css`
            font-size: 21px;

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              font-size: ${setAutomaticDesignBasedSize(21, 16)};
            `)}
          `}

          ${answers.length >= 7 && css`
            font-size: 17px;

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              font-size: ${setAutomaticDesignBasedSize(17, 16)};
            `)}
          `}

          ${keyName === 'prefecture' && css`
            ${answers.length === 4 && css`
              font-size: 17px;

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                font-size: ${setAutomaticDesignBasedSize(17, 16)};
              `)}
            `}

            ${(answers.length === 5 || answers.length === 6) && css`
              font-size: 20px;

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                font-size: ${setAutomaticDesignBasedSize(20, 16)};
              `)}
            `}
          `}

          ${keyName === 'desiredRoofMaterialType' && css`
            font-size: 18px;

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              font-size: ${setAutomaticDesignBasedSize(18, 16)};
            `)}
          `}

          ${keyName === 'detailedDesiredRoofPaintType' && css`
            font-size: 20px;

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              font-size: ${setAutomaticDesignBasedSize(20, 16)};
            `)}
          `}

          ${keyName === 'desiredPeriod' && css`
            font-size: 21px;

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              font-size: ${setAutomaticDesignBasedSize(21, 16)};
            `)}
          `}

          &:hover {
            ${simpleButtonHoverStyle}
          }

          ${isActive !== null && isActive && css`
            ${simpleButtonActiveStyle}
          `}

          ${isAnswered && css`
            ${simpleButtonAnsweredStyle}
          `}
        `}
      >
        {textContents}
      </button>
      {balloonContents && (
        <div
          css={css`
            color: #fff;
            font-size: 14px;
            text-align: center;
            white-space: nowrap;
            min-width: 50px;
            padding: 6px 12px;
            background-color: #041156;
            border-radius: 6px;
            filter: drop-shadow(0 1px 4px rgb(0 0 0 / 0.6));
            position: absolute;
            bottom: calc(100% + 2px);
            right: 11px;
            opacity: 0;
            visibility: hidden;
            animation: ${setTime(0.4)}s ${setTime(3)}s forwards ${keyframes`
              from {
                opacity: 0;
                visibility: hidden;
                bottom: calc(100% - 25px) ;
              }

              to {
                opacity: 1;
                visibility: visible;
                bottom: calc(100% - 15px) ;
              }
            `};
            pointer-events: none;

            ${tablet(() => css`
              transform: translate3d(-50%, 0, 0);
            `)}

            ${mobile(() => css`
              right: -13px;
            `)}

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              font-size: ${setAutomaticDesignBasedSize(14, 14)};
              min-width: ${setAutomaticDesignBasedSize(50, 50)};
              padding: ${setAutomaticDesignBasedSize(6, 6)} ${setAutomaticDesignBasedSize(12, 12)};
              right: -18px;
            `)}

            &::before {
              content: "";
              width: 16px;
              height: 10px;
              clip-path: polygon(100% 0, 0 0, 50% 100%);
              background-color: #041156;
              position: absolute;
              top: calc(100% - 1px);
              left: 50%;
              transform: translateX(-50%);

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                width: ${setAutomaticDesignBasedSize(16, 16)};
                height: ${setAutomaticDesignBasedSize(10, 10)};
              `)}
            }
          `}
        >
          {balloonContents}
        </div>
      )}
    </div>
  );
};
